import { AlertCircle, ArrowLeft, Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '~/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { cn } from '~/lib/utils';
import Loading from '../components/base/Loading';
import Content from '../components/layout/Content';
import TreatmentSteps from '~/components/treatment/new/TreatmentSteps';
import { Checkbox } from '~/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '~/components/ui/alert-dialog';
import { useEffect, useState, useRef } from 'react';
import TreatmentVariantPractitioners from '~/components/treatment/new/TreatmentVariantPractitioners';
import { useUserStore } from '~/stores/user-store';
import { useShallow } from 'zustand/react/shallow';
import { UserRole } from '~/types/server-types/UserRole';

type GoaBillingType = 'STATIC' | 'DYNAMIC';

type SelectedGoaTreatment = {
    goaTreatmentNo: string;
    billingType: GoaBillingType;
    factor: number;
    amount: number;
};

const TreatmentVariant = () => {
    const { userRole } = useUserStore(
        useShallow((state) => ({
            userRole: state.user?.role
        }))
    );
    const shouldDisableAllInputs = userRole !== UserRole.ADMIN;

    const params = useParams();
    const navigate = useNavigate();
    const formRef = useRef<HTMLFormElement>(null);

    const treatmentId = params.treatmentId!;
    const variantId = params.variantId!;

    const treatmentQuery = trpc.treatments.getOne.useQuery(
        {
            id: treatmentId,
        },
        {
            suspense: true,
        }
    );

    const variantQuery = trpc.treatmentVariants.getOne.useQuery(
        {
            id: variantId,
        },
        {
            suspense: true,
        }
    );

    const storesQuery = trpc.stores.getAll.useQuery();

    // GOA treatment search state
    const [goaSearchQuery, setGoaSearchQuery] = useState('');
    const [goaComboboxOpen, setGoaComboboxOpen] = useState(false);

    // Material search state
    const [materialSearchQuery, setMaterialSearchQuery] = useState('');
    const [materialComboboxOpen, setMaterialComboboxOpen] = useState(false);
    const [selectedMaterials, setSelectedMaterials] = useState<{materialId: string, quantity: number}[]>([]);
    const [hasInitializedMaterialSelection, setHasInitializedMaterialSelection] = useState(false);

    // Query for searching GOA treatments
    const goaTreatmentsQuery = trpc.treatmentVariants.searchGoaTreatments.useQuery(
        { search: goaSearchQuery, limit: 10 },
        { enabled: goaComboboxOpen } // Only fetch when combobox is open
    );

    // Query for searching materials
    const materialsQuery = trpc.materials.searchMaterials.useQuery(
        { search: materialSearchQuery, limit: 10 },
        { enabled: materialComboboxOpen }
    );

    // Query to get the currently selected materials for display
    const selectedMaterialsQuery = trpc.materials.getMaterialsByIds.useQuery(
        { ids: selectedMaterials.map(m => m.materialId) },
        { enabled: selectedMaterials.length > 0 }
    );

    const updateTreatmentVariantMutation = trpc.treatmentVariants.update.useMutation({
        onSuccess: () => {
            toast.success('Variant updated successfully.');
            variantQuery.refetch();
            treatmentQuery.refetch();
        },
        onError: (error) => {
            toast.error(`Failed to update variant: ${error.message}`);
        },
    });

    const archiveTreatmentVariantMutation = trpc.treatmentVariants.archive.useMutation({
        onSuccess: () => {
            toast.success('Variant archived successfully.');
        },
        onError: (error) => {
            toast.error(`Failed to archive variant: ${error.message}`);
        },
    });

    const handleArchiveTreatmentVariant = async () => {
        if (userRole !== UserRole.ADMIN) {
            toast.error("You do not have permission to do this.");
            return;
        }
        await archiveTreatmentVariantMutation.mutateAsync({
            id: variantId,
        });
        navigate(`/treatment/${treatmentId}`);
    };

    const handleUpdateTreatmentVariant = async (data: { name: string; shortDescription: string; longDescription?: string, priceCents: number, isPackageDeal: boolean, unitQuantity: number, launchDate: string | null, hideFromPublic: boolean, hideFromStaff: boolean, storeIds: number[], goaTreatmentAssociations: SelectedGoaTreatment[], materialAssociations: { materialId: string, quantity: number }[] }) => {
        if (userRole !== UserRole.ADMIN) {
            toast.error("You do not have permission to do this.");
            return;
        }
        updateTreatmentVariantMutation.mutate({
            id: variantId,
            isDigital: isDigital === undefined ? false : isDigital,
            ...data,
        });
    };

    const [isDigital, setIsDigital] = useState<boolean | undefined>(undefined);
    const [selectedGoaTreatments, setSelectedGoaTreatments] = useState<SelectedGoaTreatment[]>([]);
    const [hasInitializedGoaSelection, setHasInitializedGoaSelection] = useState(false);

    // Query to get the currently selected GOA treatments for display
    const selectedGoaTreatmentsQuery = trpc.treatmentVariants.getGoaTreatmentsByNos.useQuery(
        { goaNos: selectedGoaTreatments.map(g => g.goaTreatmentNo) },
        { enabled: selectedGoaTreatments.length > 0 }
    );

    useEffect(() => {
        if (isDigital === undefined && variantQuery?.data?.data && variantQuery.isLoading === false) {
            setIsDigital(variantQuery.data.data.isDigital);
        }
    }, [variantQuery]);

    useEffect(() => {
        if (!hasInitializedGoaSelection && variantQuery?.data?.data && variantQuery.isLoading === false) {
            const goaTreatmentAssociations = variantQuery.data.data.goaTreatmentAssociations?.map(
                (assoc) => ({
                    goaTreatmentNo: assoc.goaTreatmentNo,
                    billingType: (assoc.billingType ?? 'STATIC') as GoaBillingType,
                    factor: Number(assoc.factor ?? 2.3),
                    amount: assoc.amount ?? 1,
                })
            ) ?? [];
            setSelectedGoaTreatments(goaTreatmentAssociations);
            setHasInitializedGoaSelection(true);
        }
    }, [variantQuery, hasInitializedGoaSelection]);

    useEffect(() => {
        if (!hasInitializedMaterialSelection && variantQuery?.data?.data && variantQuery.isLoading === false) {
            const materials = variantQuery.data.data.materialAssociations?.map(
                (assoc) => ({ materialId: assoc.materialId, quantity: Number(assoc.quantity) })
            ) ?? [];
            setSelectedMaterials(materials);
            setHasInitializedMaterialSelection(true);
        }
    }, [variantQuery, hasInitializedMaterialSelection]);

    // Helper to sort GOÄ treatments by their figure/number
    const sortGoaTreatmentsByNumber = (treatments: SelectedGoaTreatment[]): SelectedGoaTreatment[] => {
        return [...treatments].sort((a, b) => {
            // Extract numeric part for proper sorting (e.g., "1" before "10", "A1" properly handled)
            const aNum = parseFloat(a.goaTreatmentNo.replace(/[^\d.]/g, '')) || 0;
            const bNum = parseFloat(b.goaTreatmentNo.replace(/[^\d.]/g, '')) || 0;
            if (aNum !== bNum) {
                return aNum - bNum;
            }
            // Fall back to string comparison if numbers are equal
            return a.goaTreatmentNo.localeCompare(b.goaTreatmentNo);
        });
    };

    const collectFormDataAndSave = () => {
        if (!formRef.current) return;
        
        const formData = new FormData(formRef.current);
        const priceValue = Number(formData.get("price")).toFixed(2);
        const fields = {
            name: formData.get('name') as string,
            shortDescription: formData.get('shortDescription') as string,
            longDescription: formData.get('longDescription') as string,
            priceCents: Math.max(0, Math.round(100 * parseFloat(priceValue))),
            launchDate: formData.get("launchDate") ? (formData.get("launchDate") as string) : null,
            hideFromPublic: formData.get('hideFromPublic') === 'on',
            hideFromStaff: formData.get('hideFromStaff') === 'on',
            isPackageDeal: formData.get('isPackageDeal') === 'on',
            unitQuantity: Number(formData.get('unitQuantity') ?? 0),
            storeIds: Array.from(formData.getAll('storeIds')).map(id => Number(id)),
            goaTreatmentAssociations: selectedGoaTreatments,
            materialAssociations: selectedMaterials,
        };
        handleUpdateTreatmentVariant(fields);
    };

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        collectFormDataAndSave();
    };

    if (variantQuery.isLoading || treatmentQuery.isLoading) return <Loading />;

    if (!variantQuery.data || !treatmentQuery.data)
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className='flex flex-col items-center justify-center min-h-[60vh] text-center'>
                        <div className='p-4 bg-red-100 rounded-full mb-6'>
                            <AlertCircle className='h-16 w-16 text-red-600' />
                        </div>
                        <h1 className='text-4xl font-bold text-gray-900 mb-4'>Ups...</h1>
                        <p className='text-xl text-gray-600 mb-8'>Etwas ist schief gelaufen</p>
                        <Button variant='default' onClick={() => navigate(-1)}>
                            Zurück
                        </Button>
                    </div>
                </div>
            </Content>
        );

    // Get sorted GOÄ treatments for display
    const sortedGoaTreatmentsForDisplay = selectedGoaTreatmentsQuery.data?.data
        ? [...selectedGoaTreatmentsQuery.data.data].sort((a, b) => {
            const aNum = parseFloat(a.goaNo.replace(/[^\d.]/g, '')) || 0;
            const bNum = parseFloat(b.goaNo.replace(/[^\d.]/g, '')) || 0;
            if (aNum !== bNum) {
                return aNum - bNum;
            }
            return a.goaNo.localeCompare(b.goaNo);
        })
        : [];

    const SaveButton = () => (
        <Button
            type='button'
            className='bg-primary-400 hover:bg-primary-500 text-white! transition-all'
            disabled={updateTreatmentVariantMutation.isPending || shouldDisableAllInputs}
            onClick={collectFormDataAndSave}
        >
            {updateTreatmentVariantMutation.isPending && <Loader2 className='animate-spin mr-2 h-4 w-4' />}
            Save Variant
        </Button>
    );

    // Dedicated save function for Verrechnung tab - uses current variant data for form fields
    const saveVerrechnungData = () => {
        const variant = variantQuery.data?.data;
        if (!variant) return;

        // Read current form values from DOM if available, otherwise use variant data
        const nameInput = document.getElementById('name') as HTMLInputElement;
        const shortDescInput = document.getElementById('shortDescription') as HTMLInputElement;
        const longDescInput = document.getElementById('longDescription') as HTMLTextAreaElement;
        const priceInput = document.getElementById('price') as HTMLInputElement;
        const launchDateInput = document.getElementById('launchDate') as HTMLInputElement;
        const unitQuantityInput = document.getElementById('unitQuantity') as HTMLInputElement;
        const hideFromPublicCheckbox = document.getElementById('hideFromPublic') as HTMLInputElement;
        const hideFromStaffCheckbox = document.getElementById('hideFromStaff') as HTMLInputElement;
        const isPackageDealCheckbox = document.getElementById('isPackageDeal') as HTMLInputElement;

        // Get selected store IDs from checkboxes
        const storeCheckboxes = document.querySelectorAll<HTMLInputElement>('input[name="storeIds"]:checked');
        const storeIds = Array.from(storeCheckboxes).map(cb => Number(cb.value));

        const fields = {
            name: nameInput?.value || variant.name,
            shortDescription: shortDescInput?.value || variant.shortDescription,
            longDescription: longDescInput?.value || variant.longDescription,
            priceCents: Math.max(0, Math.round(100 * parseFloat(priceInput?.value || String(variant.priceCents / 100)))),
            launchDate: launchDateInput?.value || null,
            hideFromPublic: hideFromPublicCheckbox?.checked ?? variant.hideFromPublic,
            hideFromStaff: hideFromStaffCheckbox?.checked ?? variant.hideFromStaff,
            isPackageDeal: isPackageDealCheckbox?.checked ?? variant.isPackageDeal,
            unitQuantity: Number(unitQuantityInput?.value) || variant.unitQuantity,
            storeIds: storeIds.length > 0 ? storeIds : variant.storeAssociations.map(s => s.storeId),
            goaTreatmentAssociations: selectedGoaTreatments,
            materialAssociations: selectedMaterials,
        };
        handleUpdateTreatmentVariant(fields);
    };

    const SaveVerrechnungButton = () => (
        <Button
            type='button'
            className='bg-primary-400 hover:bg-primary-500 text-white! transition-all'
            disabled={updateTreatmentVariantMutation.isPending || shouldDisableAllInputs}
            onClick={saveVerrechnungData}
        >
            {updateTreatmentVariantMutation.isPending && <Loader2 className='animate-spin mr-2 h-4 w-4' />}
            Save Variant
        </Button>
    );

    return (
        <Content>
            <div className='max-w-4xl container mx-auto'>
                <div className='flex justify-between items-center mb-4'>
                    <div className='flex flex-row items-center gap-3'>
                        <Button
                            variant='ghost'
                            size='icon'
                            onClick={() => navigate(`/treatment/${treatmentId}`)}
                            className='w-8 h-8 border-gray-300 hover:bg-gray-50'
                        >
                            <ArrowLeft />
                        </Button>
                        <h2 className='text-3xl font-semibold mb-1.5'>{variantQuery.data.data.name}</h2>
                    </div>
                </div>

                <Tabs defaultValue='allgemein' className='w-full'>
                    <TabsList className='mb-4'>
                        <TabsTrigger value='allgemein'>Allgemein</TabsTrigger>
                        <TabsTrigger value='steps'>Steps</TabsTrigger>
                        <TabsTrigger value='behandler'>Behandler</TabsTrigger>
                        <TabsTrigger value='verrechnung'>Verrechnung</TabsTrigger>
                    </TabsList>

                    {/* Tab: Allgemein */}
                    <TabsContent value='allgemein'>
                        <Card className='bg-white shadow-sm'>
                            <CardContent>
                                <form
                                    ref={formRef}
                                    onSubmit={handleFormSubmit}
                                >
                                    <div className='space-y-6'>
                                        <div className='space-y-2'>
                                            <label htmlFor='name' className='text-sm font-medium'>
                                                Name
                                            </label>
                                            <Input id='name' name='name' className='w-full' defaultValue={variantQuery.data.data.name} required disabled={shouldDisableAllInputs} />
                                        </div>

                                        <div className='space-y-2'>
                                            <label htmlFor='shortDescription' className='text-sm font-medium'>
                                                Short Description
                                            </label>
                                            <Input id='shortDescription' name='shortDescription' className='w-full' defaultValue={variantQuery.data.data.shortDescription} disabled={shouldDisableAllInputs} />
                                        </div>

                                        <div className='space-y-2'>
                                            <label htmlFor='longDescription' className='text-sm font-medium'>
                                                Long Description
                                            </label>
                                            <textarea
                                                id='longDescription'
                                                name='longDescription'
                                                defaultValue={variantQuery.data.data.longDescription}
                                                className={`w-full min-h-[150px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${shouldDisableAllInputs ? 'text-gray-400' : ''}`}
                                                disabled={shouldDisableAllInputs}
                                            />
                                        </div>

                                        <div className='space-y-2'>
                                            <label htmlFor='price' className='text-sm font-medium'>
                                                Price (€)
                                            </label>
                                            <Input
                                                id='price'
                                                name='price'
                                                type='number'
                                                min='0'
                                                step='0.01'
                                                className='w-full'
                                                defaultValue={variantQuery.data.data.priceCents / 100.00}
                                                required
                                                disabled={shouldDisableAllInputs}
                                            />
                                        </div>

                                        <div className='space-y-4'>
                                            <div className='space-y-2'>
                                                <label htmlFor='launchDate' className='text-sm font-medium'>
                                                    Launch Date
                                                </label>
                                                <p className='text-xs text-gray-500'>
                                                    If set, timeslots will only be shown for this date and later in the booking funnel.
                                                </p>
                                                <div className='flex gap-2'>
                                                    <Input
                                                        id='launchDate'
                                                        name='launchDate'
                                                        type='date'
                                                        className='w-full'
                                                        defaultValue={variantQuery.data.data.launchDate ? new Date(variantQuery.data.data.launchDate).toISOString().split('T')[0] : ''}
                                                        disabled={shouldDisableAllInputs}
                                                    />
                                                    {variantQuery.data.data.launchDate && (
                                                        <Button
                                                            type='button'
                                                            variant='outline'
                                                            size='icon'
                                                            onClick={() => {
                                                                const input = document.getElementById('launchDate') as HTMLInputElement;
                                                                if (input) {
                                                                    input.value = '';
                                                                }
                                                            }}
                                                            disabled={shouldDisableAllInputs}
                                                        >
                                                            ×
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className='flex items-center space-x-2'>
                                                <Checkbox
                                                    id='hideFromPublic'
                                                    name='hideFromPublic'
                                                    defaultChecked={variantQuery.data.data.hideFromPublic}
                                                    disabled={shouldDisableAllInputs}
                                                />
                                                <label htmlFor='hideFromPublic' className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
                                                    Hide from public
                                                </label>
                                            </div>

                                            <div className='flex items-center space-x-2'>
                                                <Checkbox
                                                    id='hideFromStaff'
                                                    name='hideFromStaff'
                                                    defaultChecked={variantQuery.data.data.hideFromStaff}
                                                    disabled={shouldDisableAllInputs}
                                                />
                                                <label htmlFor='hideFromStaff' className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
                                                    Hide from staff
                                                </label>
                                            </div>
                                            <div className='flex items-center space-x-2'>
                                                <Checkbox
                                                    id='isPackageDeal'
                                                    name='isPackageDeal'
                                                    defaultChecked={variantQuery.data.data.isPackageDeal}
                                                    disabled={shouldDisableAllInputs}
                                                />
                                                <label htmlFor='isPackageDeal' className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
                                                    Is package / bundle?
                                                </label>
                                            </div>
                                            <div className='flex items-center space-x-2 mt-2'>
                                                <label htmlFor='unitQuantity' className='text-sm font-medium'>
                                                    Unit Quantity
                                                </label>
                                                <Input
                                                    id='unitQuantity'
                                                    name='unitQuantity'
                                                    type='number'
                                                    min={1}
                                                    max={25}
                                                    step={1}
                                                    defaultValue={variantQuery.data.data.unitQuantity}
                                                    className='w-20'
                                                    required
                                                    disabled={shouldDisableAllInputs}
                                                />
                                            </div>
                                            <div className='flex items-center space-x-2'>
                                                <Checkbox
                                                    id='isDigital'
                                                    name='isDigital'
                                                    checked={isDigital}
                                                    onCheckedChange={(val) => {
                                                        setIsDigital(val === 'indeterminate' ? false : val)
                                                    }}
                                                    disabled={shouldDisableAllInputs}
                                                />
                                                <label htmlFor='isDigital' className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
                                                    Only Digital
                                                </label>
                                            </div>
                                        </div>

                                        <div className='space-y-2'>
                                            <label className='text-sm font-medium'>
                                                Stores
                                            </label>
                                            <div className='space-y-2'>
                                                {storesQuery.isLoading ? (
                                                    <div className='text-sm text-gray-500'>Loading stores...</div>
                                                ) : (
                                                    <>
                                                        {storesQuery.data?.data.map((store) => {
                                                            const isSelected = variantQuery.data.data.storeAssociations.some(
                                                                (assoc) => assoc.storeId === store.id
                                                            );
                                                            return (
                                                                <div key={store.id} className='flex items-center space-x-2'>
                                                                    <Checkbox
                                                                        id={`store-${store.id}`}
                                                                        name='storeIds'
                                                                        value={store.id}
                                                                        defaultChecked={isSelected}
                                                                        disabled={isDigital || shouldDisableAllInputs}
                                                                    />
                                                                    <label
                                                                        htmlFor={`store-${store.id}`}
                                                                        className='text-sm'
                                                                    >
                                                                        {store.name}
                                                                    </label>
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </div>
                                            {isDigital && (
                                                <label className='text-sm font-medium'>
                                                    This treatment variant is set to be Digital. Because of this, the stores feature is currently off.
                                                </label>
                                            )}
                                        </div>

                                        <div className='flex justify-end mt-6'>
                                            <SaveButton />
                                        </div>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab: Steps */}
                    <TabsContent value='steps'>
                        <Card className='bg-white shadow-sm'>
                            <CardContent>
                                <TreatmentSteps variantId={params.variantId!} />
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab: Behandler */}
                    <TabsContent value='behandler'>
                        <Card className='bg-white shadow-sm'>
                            <CardContent>
                                <TreatmentVariantPractitioners variantId={params.variantId!} />
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab: Verrechnung */}
                    <TabsContent value='verrechnung'>
                        <Card className='bg-white shadow-sm'>
                            <CardContent>
                                <div className='space-y-6'>
                                    <div className='space-y-2'>
                                        <label className='text-sm font-medium'>
                                            Default GOÄ Treatments for Billing Drafts
                                        </label>
                                        <p className='text-xs text-gray-500'>
                                            Select the GOÄ treatments that should be used by default when creating billing drafts for this treatment variant. Configure billing type (STATIC uses fixed factor, DYNAMIC calculates based on treatment) and GOÄ factor for each.
                                        </p>
                                        
                                        {/* Selected GOA treatments with billing type and factor */}
                                        {selectedGoaTreatments.length > 0 && (
                                            <div className='space-y-3 mb-2'>
                                                {sortedGoaTreatmentsForDisplay.map((goaTreatment) => {
                                                    const selectedGoa = selectedGoaTreatments.find(g => g.goaTreatmentNo === goaTreatment.goaNo);
                                                    if (!selectedGoa) return null;
                                                    return (
                                                        <div
                                                            key={goaTreatment.goaNo}
                                                            className='flex items-center gap-3 p-3 border rounded-lg bg-gray-50'
                                                        >
                                                            <div className='flex-1'>
                                                                <div className='font-medium text-sm'>{goaTreatment.goaNo} - {goaTreatment.name}</div>
                                                                <div className='text-xs text-gray-500'>
                                                                    Base: {goaTreatment.basePoints} Pkt.
                                                                    {selectedGoa.billingType === 'STATIC' && (
                                                                        <span className='ml-2 font-medium text-gray-700'>
                                                                            | Total: {(goaTreatment.basePoints * selectedGoa.factor * selectedGoa.amount).toFixed(1)} Pkt.
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className='flex items-center gap-2'>
                                                                <div className='flex items-center gap-1'>
                                                                    <span className='text-xs text-gray-500'>Anzahl:</span>
                                                                    <Input
                                                                        type='number'
                                                                        min='1'
                                                                        step='1'
                                                                        value={selectedGoa.amount}
                                                                        onChange={(e) => {
                                                                            const newAmount = Math.max(1, Math.round(parseInt(e.target.value) || 1));
                                                                            setSelectedGoaTreatments(
                                                                                selectedGoaTreatments.map(g =>
                                                                                    g.goaTreatmentNo === goaTreatment.goaNo
                                                                                        ? { ...g, amount: newAmount }
                                                                                        : g
                                                                                )
                                                                            );
                                                                        }}
                                                                        className='h-8 w-16 text-sm'
                                                                        disabled={shouldDisableAllInputs}
                                                                    />
                                                                </div>
                                                                <Select
                                                                    value={selectedGoa.billingType}
                                                                    onValueChange={(value: GoaBillingType) => {
                                                                        setSelectedGoaTreatments(
                                                                            selectedGoaTreatments.map(g =>
                                                                                g.goaTreatmentNo === goaTreatment.goaNo
                                                                                    ? { ...g, billingType: value }
                                                                                    : g
                                                                            )
                                                                        );
                                                                    }}
                                                                    disabled={shouldDisableAllInputs}
                                                                >
                                                                    <SelectTrigger className='w-[110px] h-8'>
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value='STATIC'>Static</SelectItem>
                                                                        <SelectItem value='DYNAMIC'>Dynamic</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                                {selectedGoa.billingType === 'STATIC' && (
                                                                    <div className='flex items-center gap-1'>
                                                                        <span className='text-xs text-gray-500'>Factor:</span>
                                                                        <Input
                                                                            type='number'
                                                                            min='0.1'
                                                                            step='0.1'
                                                                            value={selectedGoa.factor}
                                                                            onChange={(e) => {
                                                                                const newFactor = parseFloat(e.target.value) || 2.3;
                                                                                setSelectedGoaTreatments(
                                                                                    selectedGoaTreatments.map(g =>
                                                                                        g.goaTreatmentNo === goaTreatment.goaNo
                                                                                            ? { ...g, factor: newFactor }
                                                                                            : g
                                                                                    )
                                                                                );
                                                                            }}
                                                                            className='h-8 w-20 text-sm'
                                                                            disabled={shouldDisableAllInputs}
                                                                        />
                                                                    </div>
                                                                )}
                                                                <Button
                                                                    type='button'
                                                                    variant='ghost'
                                                                    size='icon'
                                                                    className='h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50'
                                                                    onClick={() => {
                                                                        setSelectedGoaTreatments(
                                                                            selectedGoaTreatments.filter((g) => g.goaTreatmentNo !== goaTreatment.goaNo)
                                                                        );
                                                                    }}
                                                                    disabled={shouldDisableAllInputs}
                                                                >
                                                                    <X className='h-4 w-4' />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {/* Show loading for GOA numbers not yet loaded */}
                                                {selectedGoaTreatments
                                                    .filter((g) => !selectedGoaTreatmentsQuery.data?.data.some((goa) => goa.goaNo === g.goaTreatmentNo))
                                                    .map((goa) => (
                                                        <div key={goa.goaTreatmentNo} className='flex items-center gap-3 p-3 border rounded-lg bg-gray-50'>
                                                            <div className='flex-1 text-sm text-gray-500'>Loading {goa.goaTreatmentNo}...</div>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                        
                                        <Popover open={goaComboboxOpen} onOpenChange={setGoaComboboxOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant='outline'
                                                    role='combobox'
                                                    aria-expanded={goaComboboxOpen}
                                                    className='w-full justify-between font-normal'
                                                    disabled={shouldDisableAllInputs}
                                                >
                                                    {selectedGoaTreatments.length === 0
                                                        ? 'Select GOÄ treatments...'
                                                        : `${selectedGoaTreatments.length} GOÄ treatment${selectedGoaTreatments.length === 1 ? '' : 's'} selected`}
                                                    <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className='w-[400px] p-0' align='start'>
                                                <Command shouldFilter={false}>
                                                    <CommandInput
                                                        placeholder='Search GOÄ treatments...'
                                                        value={goaSearchQuery}
                                                        onValueChange={setGoaSearchQuery}
                                                    />
                                                    <CommandList>
                                                        <CommandEmpty>
                                                            {goaTreatmentsQuery.isLoading ? 'Loading...' : 'No GOÄ treatment found.'}
                                                        </CommandEmpty>
                                                        <CommandGroup>
                                                            {selectedGoaTreatments.length > 0 && (
                                                                <CommandItem
                                                                    value='clear-all'
                                                                    onSelect={() => {
                                                                        setSelectedGoaTreatments([]);
                                                                    }}
                                                                    className='text-red-600'
                                                                >
                                                                    <X className='mr-2 h-4 w-4' />
                                                                    Clear all selections
                                                                </CommandItem>
                                                            )}
                                                            {goaTreatmentsQuery.data?.data.map((goaTreatment) => {
                                                                const isSelected = selectedGoaTreatments.some(g => g.goaTreatmentNo === goaTreatment.goaNo);
                                                                return (
                                                                    <CommandItem
                                                                        key={goaTreatment.goaNo}
                                                                        value={goaTreatment.goaNo}
                                                                        onSelect={() => {
                                                                            if (isSelected) {
                                                                                setSelectedGoaTreatments(
                                                                                    selectedGoaTreatments.filter((g) => g.goaTreatmentNo !== goaTreatment.goaNo)
                                                                                );
                                                                            } else {
                                                                                // Use the GOÄ item's default billing type
                                                                                const defaultBillingType = (goaTreatment.billingType as GoaBillingType) || 'STATIC';
                                                                                const newSelection = sortGoaTreatmentsByNumber([
                                                                                    ...selectedGoaTreatments,
                                                                                    { goaTreatmentNo: goaTreatment.goaNo, billingType: defaultBillingType, factor: 2.3, amount: 1 }
                                                                                ]);
                                                                                setSelectedGoaTreatments(newSelection);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Check
                                                                            className={cn(
                                                                                'mr-2 h-4 w-4',
                                                                                isSelected ? 'opacity-100' : 'opacity-0'
                                                                            )}
                                                                        />
                                                                        {goaTreatment.goaNo} - {goaTreatment.name} ({goaTreatment.basePoints} Pkt.)
                                                                    </CommandItem>
                                                                );
                                                            })}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </div>

                                    <div className='space-y-2'>
                                        <label className='text-sm font-medium'>
                                            Default Materials for Billing Drafts
                                        </label>
                                        <p className='text-xs text-gray-500'>
                                            Select the materials that should be used by default when creating billing drafts for this treatment variant. You can specify quantities for each material.
                                        </p>
                                        
                                        {/* Selected materials as badges with quantity */}
                                        {selectedMaterials.length > 0 && (
                                            <div className='flex flex-wrap gap-2 mb-2'>
                                                {selectedMaterialsQuery.data?.data.map((material) => {
                                                    const selectedMaterial = selectedMaterials.find(m => m.materialId === material.id);
                                                    return (
                                                        <Badge
                                                            key={material.id}
                                                            variant='secondary'
                                                            className='flex items-center gap-2 pr-1 py-1'
                                                        >
                                                            <span>{material.name}</span>
                                                            <Input
                                                                type='number'
                                                                min='1'
                                                                step='1'
                                                                value={selectedMaterial?.quantity ?? 1}
                                                                onChange={(e) => {
                                                                    const newQuantity = parseInt(e.target.value) || 1;
                                                                    setSelectedMaterials(
                                                                        selectedMaterials.map(m =>
                                                                            m.materialId === material.id
                                                                                ? { ...m, quantity: newQuantity }
                                                                                : m
                                                                        )
                                                                    );
                                                                }}
                                                                className='h-6 w-20 text-xs'
                                                                disabled={shouldDisableAllInputs}
                                                            />
                                                            <span className='text-xs text-gray-500'>
                                                                ({Number(material.unitPriceGross).toFixed(2)} € × {selectedMaterial?.quantity ?? 1} = {(Number(material.unitPriceGross) * (selectedMaterial?.quantity ?? 1)).toFixed(2)} €)
                                                            </span>
                                                            <Button
                                                                type='button'
                                                                variant='ghost'
                                                                size='icon'
                                                                className='h-4 w-4 p-0 hover:bg-transparent'
                                                                onClick={() => {
                                                                    setSelectedMaterials(
                                                                        selectedMaterials.filter((m) => m.materialId !== material.id)
                                                                    );
                                                                }}
                                                                disabled={shouldDisableAllInputs}
                                                            >
                                                                <X className='h-3 w-3' />
                                                            </Button>
                                                        </Badge>
                                                    );
                                                })}
                                                {/* Show loading badges for materials not yet loaded */}
                                                {selectedMaterials
                                                    .filter((m) => !selectedMaterialsQuery.data?.data.some((mat) => mat.id === m.materialId))
                                                    .map((material) => (
                                                        <Badge key={material.materialId} variant='secondary' className='flex items-center gap-1'>
                                                            Loading...
                                                        </Badge>
                                                    ))}
                                            </div>
                                        )}
                                        
                                        <Popover open={materialComboboxOpen} onOpenChange={setMaterialComboboxOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant='outline'
                                                    role='combobox'
                                                    aria-expanded={materialComboboxOpen}
                                                    className='w-full justify-between font-normal'
                                                    disabled={shouldDisableAllInputs}
                                                >
                                                    {selectedMaterials.length === 0
                                                        ? 'Select materials...'
                                                        : `${selectedMaterials.length} material${selectedMaterials.length === 1 ? '' : 's'} selected`}
                                                    <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className='w-[400px] p-0' align='start'>
                                                <Command shouldFilter={false}>
                                                    <CommandInput
                                                        placeholder='Search materials...'
                                                        value={materialSearchQuery}
                                                        onValueChange={setMaterialSearchQuery}
                                                    />
                                                    <CommandList>
                                                        <CommandEmpty>
                                                            {materialsQuery.isLoading ? 'Loading...' : 'No material found.'}
                                                        </CommandEmpty>
                                                        <CommandGroup>
                                                            {selectedMaterials.length > 0 && (
                                                                <CommandItem
                                                                    value='clear-all'
                                                                    onSelect={() => {
                                                                        setSelectedMaterials([]);
                                                                    }}
                                                                    className='text-red-600'
                                                                >
                                                                    <X className='mr-2 h-4 w-4' />
                                                                    Clear all selections
                                                                </CommandItem>
                                                            )}
                                                            {materialsQuery.data?.data.map((material) => {
                                                                const isSelected = selectedMaterials.some(m => m.materialId === material.id);
                                                                return (
                                                                    <CommandItem
                                                                        key={material.id}
                                                                        value={material.id}
                                                                        onSelect={() => {
                                                                            if (isSelected) {
                                                                                setSelectedMaterials(
                                                                                    selectedMaterials.filter((m) => m.materialId !== material.id)
                                                                                );
                                                                            } else {
                                                                                setSelectedMaterials([...selectedMaterials, { materialId: material.id, quantity: 1 }]);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Check
                                                                            className={cn(
                                                                                'mr-2 h-4 w-4',
                                                                                isSelected ? 'opacity-100' : 'opacity-0'
                                                                            )}
                                                                        />
                                                                        {material.name} ({Number(material.unitPriceGross).toFixed(2)} €)
                                                                    </CommandItem>
                                                                );
                                                            })}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </div>

                                    <div className='flex justify-end mt-6'>
                                        <SaveVerrechnungButton />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                {/* Danger Zone - Always visible below tabs */}
                <Card className='bg-white shadow-sm border-red-200 mt-8'>
                    <CardContent>
                        <h3 className='text-lg font-semibold text-red-600 mb-4'>Danger Zone</h3>
                        <div className='space-y-4'>
                            <div className='flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50'>
                                <div>
                                    <h4 className='font-medium text-red-600'>Delete Variant</h4>
                                    <p className='text-sm text-red-600'>
                                        This will delete this treatment variant. This action cannot be undone.
                                    </p>
                                </div>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant='destructive' disabled={shouldDisableAllInputs}>Delete</Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This will archive the variant "{variantQuery.data.data.name}" and hide it from the system.
                                                This action cannot be easily undone.
                                                Existing appointments with this variant will STILL be in the system, but future appointments cannot be booked with this variant.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={handleArchiveTreatmentVariant}
                                                className='bg-red-600 hover:bg-red-700'
                                            >
                                                {archiveTreatmentVariantMutation.isPending ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Deleting...
                                                    </>
                                                ) : (
                                                    'Delete Variant'
                                                )}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Content>
    );
};

export default TreatmentVariant;
